import React, { useEffect } from "react";
import { Session } from "@supabase/supabase-js";

import supabase from "../utils/supabase";
import useUserStore from "../stores/user";

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const setUser = useUserStore((state) => state.setUser);
  const setAuthResolved = useUserStore((state) => state.setAuthResolved);

  async function syncGoogleAccessToken() {
    try {
      const { data, error } = await supabase.auth.getSession();

      if (error) {
        console.error("Error getting session:", error);
        return;
      }

      if (data?.session) {
        // Try to get the provider token
        let googleAccessToken = data.session.provider_token;

        // If no provider token, try to refresh the session
        if (!googleAccessToken) {
          const { data: refreshData, error: refreshError } =
            await supabase.auth.refreshSession();

          if (refreshError) {
            console.error("Error refreshing session:", refreshError);
            return;
          }

          googleAccessToken = refreshData.session?.provider_token;
        }

        if (!googleAccessToken) {
          console.error("No Google access token found");
          return;
        }

        await supabase.functions.invoke("syncGoogleToken", {
          body: {
            googleAccessToken,
          },
        });
      }
    } catch (error) {
      console.error("Error in getGoogleAccessToken:", error);
    }
  }

  // Apply a restored/refreshed session's user into the store, merging LTI
  // metadata when present. This is the exact logic the SIGNED_IN handler has
  // always used, factored out so cold-load restore reuses it identically.
  function applySessionUser(session: Session | null) {
    const authProvider = localStorage.getItem("authProvider");
    const ltiUserData = localStorage.getItem("ltiUserData");

    if (authProvider === "lti" && ltiUserData && session?.user) {
      try {
        const parsedLtiData = JSON.parse(ltiUserData);
        // Merge LTI user data with session user data
        const mergedUser = {
          ...session.user,
          user_metadata: {
            ...session.user.user_metadata,
            ...parsedLtiData,
          },
        };
        setUser(mergedUser);
      } catch (e) {
        console.warn("Failed to merge LTI user data:", e);
        setUser(session?.user);
      }
    } else {
      setUser(session?.user);
    }
  }

  useEffect(() => {
    // Cold-load restore: the SIGNED_IN-only handler below never fires for a
    // persisted session on a fresh page load, so guards would see no user and
    // bounce to /login-portal. Pull the session explicitly, then mark auth
    // resolved so guards can distinguish "still resolving" from "logged out".
    let active = true;
    supabase.auth
      .getSession()
      .then(({ data, error }) => {
        if (!active) return;
        if (error) {
          console.error("Error restoring session:", error);
          return;
        }
        if (data?.session?.user) {
          applySessionUser(data.session);
        }
      })
      .catch((error) => {
        console.error("Error restoring session:", error);
      })
      .finally(() => {
        if (active) setAuthResolved(true);
      });

    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY") {
        // CT-5: a recovery link creates a session; send the user to the
        // reset form (never on to a dashboard) even if the email redirect
        // landed somewhere else.
        applySessionUser(session);
        if (window.location.pathname !== "/reset-password") {
          window.location.replace("/reset-password");
        }
        setAuthResolved(true);
        return;
      }

      // INITIAL_SESSION + TOKEN_REFRESHED restore the user the same way
      // SIGNED_IN does; SIGNED_IN additionally syncs the Google token.
      if (
        event === "SIGNED_IN" ||
        event === "INITIAL_SESSION" ||
        event === "TOKEN_REFRESHED"
      ) {
        applySessionUser(session);

        if (event === "SIGNED_IN") {
          syncGoogleAccessToken();
        }
      }

      // Any auth event means the session state is now known.
      setAuthResolved(true);
    });

    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, []);

  return children;
}
